import { useEffect, useRef, useState } from "react";
import { api, API } from "../lib/api";
import Layout from "../components/Layout";
import {
  Sparkles, Send, Plus, Trash2, MessageSquare, BookOpen, History, Check, X, CalendarPlus, BarChart3, Mail,
} from "lucide-react";
import Markdown from "../components/Markdown";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const TABS = [
  { id: "chat", label: "Chat con Aura", icon: MessageSquare },
  { id: "training", label: "Centro de Entrenamiento", icon: BookOpen },
  { id: "learnings", label: "Aprendizajes Pendientes", icon: Sparkles },
  { id: "weekly", label: "Reporte semanal", icon: BarChart3 },
  { id: "history", label: "Historial Prompt Maestro", icon: History },
];

const AuraAI = () => {
  const [tab, setTab] = useState("chat");
  return (
    <Layout title="Aura Marketing AI" subtitle="Centro de inteligencia">
      <div className="flex gap-2 mb-6 border-b border-[#E5E5E5]" data-testid="aura-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? "border-[#D99D26] text-[#0A0A0A]" : "border-transparent text-[#666] hover:text-[#0A0A0A]"}`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>
      {tab === "chat" && <ChatPanel />}
      {tab === "training" && <TrainingPanel />}
      {tab === "learnings" && <LearningsPanel />}
      {tab === "weekly" && <WeeklyReportPanel />}
      {tab === "history" && <HistoryPanel />}
    </Layout>
  );
};

const ChatPanel = () => {
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [contextType, setContextType] = useState("general");
  const [ideaToSend, setIdeaToSend] = useState(null);
  const scrollRef = useRef(null);

  const loadSessions = () => api.get("/aura/sessions").then((r) => setSessions(r.data));
  useEffect(() => { loadSessions(); }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [currentSession?.messages]);

  const newChat = async () => {
    const res = await api.post("/aura/sessions", { context_type: contextType, title: "Nueva conversación" });
    setCurrentSession(res.data);
    loadSessions();
  };

  const openSession = async (s) => {
    const res = await api.get(`/aura/sessions/${s.id}`);
    setCurrentSession(res.data);
  };

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || sending) return;
    let sess = currentSession;
    if (!sess) {
      const res = await api.post("/aura/sessions", { context_type: contextType, title: input.substring(0, 60) });
      sess = res.data;
      setCurrentSession(sess);
    }
    const userText = input;
    setInput("");
    // Optimistic update with placeholder assistant message that will be streamed into
    const userMsg = { role: "user", content: userText, created_at: new Date().toISOString() };
    const assistantMsg = { role: "assistant", content: "", created_at: new Date().toISOString(), streaming: true };
    let updated = { ...sess, messages: [...(sess.messages || []), userMsg, assistantMsg] };
    setCurrentSession(updated);
    setSending(true);

    try {
      // Stream via SSE using fetch + ReadableStream
      const token = localStorage.getItem("rubi_aura_token");
      const resp = await fetch(`${API}/aura/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          prompt: userText,
          task_type: sess.context_type || "general",
          session_id: sess.id,
        }),
      });
      if (!resp.ok || !resp.body) {
        throw new Error("Stream no disponible");
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let fullText = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Parse SSE events (data: {...}\n\n)
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const ev of events) {
          const line = ev.trim();
          if (!line.startsWith("data:")) continue;
          const jsonStr = line.slice(5).trim();
          if (!jsonStr) continue;
          try {
            const data = JSON.parse(jsonStr);
            if (data.content) {
              fullText += data.content;
              // Update last message
              setCurrentSession((cur) => {
                if (!cur) return cur;
                const msgs = [...cur.messages];
                if (msgs.length && msgs[msgs.length - 1].role === "assistant") {
                  msgs[msgs.length - 1] = { ...msgs[msgs.length - 1], content: fullText };
                }
                return { ...cur, messages: msgs };
              });
            }
            if (data.error) {
              toast.error(data.error);
            }
          } catch (_e) { /* ignore SSE parse errors */ }
        }
      }

      // Mark streaming done
      setCurrentSession((cur) => {
        if (!cur) return cur;
        const msgs = [...cur.messages];
        if (msgs.length && msgs[msgs.length - 1].role === "assistant") {
          msgs[msgs.length - 1] = { ...msgs[msgs.length - 1], streaming: false };
        }
        return { ...cur, messages: msgs };
      });

      // Persist to DB by replaying message (server-side stream doesn't auto-save)
      // We post a single message endpoint just to persist - but that would re-call AI.
      // Instead, persist directly via a save-messages endpoint. For MVP, save via send_message but skip if already streamed.
      // Simpler: don't persist (chat is ephemeral for streamed sessions). We'll reload sessions to get fresh list.
      // To persist properly, add /sessions/{id}/messages endpoint or persist on backend during stream.
      // For now: persist by upserting the messages array via PATCH.
      try {
        await api.patch(`/aura/sessions/${sess.id}/persist`, {
          user_message: userMsg.content,
          assistant_message: fullText,
        });
      } catch (_e) { /* persist is best-effort */ }
      loadSessions();
    } catch (err) {
      toast.error(err.message || "Error en stream");
      // Fallback to non-streaming on error
      try {
        const res = await api.post(`/aura/sessions/${sess.id}/message`, { content: userText });
        setCurrentSession((cur) => {
          if (!cur) return cur;
          const msgs = [...cur.messages];
          msgs[msgs.length - 1] = res.data.reply;
          return { ...cur, messages: msgs };
        });
        loadSessions();
      } catch (e2) {
        toast.error("No se pudo obtener respuesta");
      }
    } finally {
      setSending(false);
    }
  };

  const deleteSession = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("¿Eliminar sesión?")) return;
    await api.delete(`/aura/sessions/${id}`);
    if (currentSession?.id === id) setCurrentSession(null);
    loadSessions();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5 h-[calc(100vh-220px)]" data-testid="aura-chat">
      {/* Sessions sidebar */}
      <div className="aura-card-flat p-3 overflow-hidden flex flex-col">
        <button onClick={newChat} className="btn-primary w-full justify-center flex items-center gap-2 mb-3" data-testid="new-chat-btn">
          <Plus size={14} /> Nueva conversación
        </button>
        <div className="flex-1 overflow-y-auto space-y-1">
          {sessions.length === 0 ? (
            <div className="text-center text-xs text-[#666] py-6">Sin conversaciones</div>
          ) : sessions.map((s) => (
            <div
              key={s.id}
              onClick={() => openSession(s)}
              className={`group rounded-lg p-2.5 cursor-pointer ${currentSession?.id === s.id ? "bg-[#D99D26]/10" : "hover:bg-[#F0F0F0]"}`}
              data-testid={`session-${s.id}`}
            >
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{s.title}</div>
                  <div className="text-[10px] text-[#666] uppercase tracking-wider mt-0.5">{s.context_type}</div>
                </div>
                <button onClick={(e) => deleteSession(s.id, e)} className="opacity-0 group-hover:opacity-100 text-[#666] hover:text-red-600"><Trash2 size={11} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Chat area */}
      <div className="aura-card-flat flex flex-col overflow-hidden">
        <div className="border-b border-[#E5E5E5] px-5 py-3 flex justify-between items-center">
          <div>
            <div className="label-overline">{currentSession ? "Conversación" : "Nuevo"}</div>
            <div className="font-display font-medium text-base">
              {currentSession?.title || "Aura Marketing AI"}
            </div>
          </div>
          {!currentSession && (
            <select value={contextType} onChange={(e) => setContextType(e.target.value)} className="aura-input text-xs py-1 px-2 w-auto" data-testid="context-select">
              <option value="general">General</option>
              <option value="strategy">Estrategia</option>
              <option value="script">Guion</option>
              <option value="copy">Copy</option>
              <option value="analysis">Análisis</option>
              <option value="competitor">Competencia</option>
              <option value="executive">Ejecutivo</option>
            </select>
          )}
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
          {(!currentSession || !currentSession.messages?.length) && (
            <div className="text-center py-12">
              <div className="aura-logo-mark w-16 h-16 rounded-2xl mx-auto flex items-center justify-center text-white font-display font-bold text-2xl mb-4">A</div>
              <div className="font-display text-2xl font-medium text-[#0A0A0A]">Hola, soy Aura</div>
              <p className="text-[#666] mt-2 max-w-md mx-auto">
                Estratega senior, director creativo y director de guion para Rubí. Pregúntame estrategia, ideas de contenido, copies o análisis.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-2 max-w-lg mx-auto">
                {["Dame 5 hooks virales para Tampico", "Estrategia 360 para promo 4x3", "Analiza la competencia árabe", "Plan editorial 2 semanas"].map((s) => (
                  <button key={s} onClick={() => setInput(s)} className="pill pill-gray cursor-pointer hover:pill-yellow text-xs">{s}</button>
                ))}
              </div>
            </div>
          )}

          {currentSession?.messages?.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${m.role === "user" ? "bg-[#0A0A0A] text-white" : "aura-card-flat"} group relative`}>
                {m.role === "assistant" ? (
                  <>
                    <div className="markdown-content text-sm">
                      <Markdown content={m.content} />
                    </div>
                    {m.content && !m.streaming && (
                      <button
                        onClick={() => setIdeaToSend(m.content)}
                        className="mt-2 text-xs text-[#8a5e10] hover:text-[#0A0A0A] inline-flex items-center gap-1 font-medium"
                        data-testid={`send-to-calendar-${i}`}
                        title="Mover esta idea al calendario editorial"
                      >
                        <CalendarPlus size={12} /> Enviar al calendario
                      </button>
                    )}
                  </>
                ) : (
                  <div className="text-sm whitespace-pre-wrap">{m.content}</div>
                )}
              </div>
            </div>
          ))}

          {sending && (
            <div className="flex justify-start">
              <div className="aura-card-flat rounded-2xl px-4 py-3 flex items-center gap-2">
                <span className="spinner" />
                <span className="text-sm text-[#666]">Aura está pensando...</span>
              </div>
            </div>
          )}
        </div>

        <form onSubmit={send} className="border-t border-[#E5E5E5] p-4 bg-white/70 backdrop-blur-sm">
          <div className="flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Pregúntale a Aura..."
              className="aura-input flex-1"
              disabled={sending}
              data-testid="chat-input"
            />
            <button type="submit" disabled={sending || !input.trim()} className="btn-primary flex items-center gap-2" data-testid="chat-send">
              <Send size={14} /> Enviar
            </button>
          </div>
        </form>
      </div>
      {ideaToSend && <IdeaToCalendarModal content={ideaToSend} onClose={() => setIdeaToSend(null)} />}
    </div>
  );
};

const IdeaToCalendarModal = ({ content, onClose }) => {
  // Extract first non-empty line as suggested title
  const firstLine = (content || "").split("\n").map((l) => l.replace(/^[#*\s>-]+/, "").trim()).find((l) => l) || "Idea";
  const [title, setTitle] = useState(firstLine.slice(0, 120));
  const [platform, setPlatform] = useState("instagram");
  const [format, setFormat] = useState("reel");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const send = async () => {
    setSaving(true);
    try {
      await api.post("/workflow/aura/idea-to-calendar", {
        content,
        title,
        platform,
        format,
        date,
      });
      toast.success("Idea enviada al calendario editorial");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-lg p-7" data-testid="idea-to-calendar-modal">
        <div className="flex justify-between items-start mb-5">
          <div>
            <div className="label-overline">Aura → Calendario</div>
            <h2 className="font-display text-2xl font-medium">Enviar idea al calendario</h2>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="aura-label">Título de la idea</label>
            <input className="aura-input" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="idea-title" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="aura-label">Plataforma</label>
              <select className="aura-input" value={platform} onChange={(e) => setPlatform(e.target.value)}>
                {["instagram", "tiktok", "facebook", "youtube_shorts", "multi"].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="aura-label">Formato</label>
              <select className="aura-input" value={format} onChange={(e) => setFormat(e.target.value)}>
                {["reel", "post", "carrusel", "story", "video_horizontal", "live"].map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
            <div>
              <label className="aura-label">Fecha tentativa</label>
              <input type="date" className="aura-input" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
          </div>
          <div className="aura-card-flat p-3 max-h-48 overflow-y-auto text-xs">
            <Markdown content={content} />
          </div>
          <p className="text-xs text-[#666]">Esta idea quedará como <strong>&ldquo;idea&rdquo;</strong> en el calendario. Cuando la desarrolles con Guiones o Briefs, podrás eliminar la idea original.</p>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="btn-ghost">Cancelar</button>
            <button onClick={send} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="confirm-send-idea">
              {saving && <span className="spinner" />} Enviar al calendario
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const TrainingPanel = () => {
  const { user } = useAuth();
  const canEdit = user?.role === "ceo"; // Director can only view, CEO can edit
  const isDirector = user?.role === "director_marketing";
  const [active, setActive] = useState(null);
  const [versions, setVersions] = useState([]);
  const [edit, setEdit] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [kb, setKb] = useState([]);

  const load = () => {
    Promise.all([
      api.get("/prompt-master"),
      api.get("/knowledge"),
    ]).then(([pm, k]) => {
      setVersions(pm.data);
      const masters = pm.data.filter((p) => p.section === "master" && p.is_active);
      const cur = masters[0] || pm.data.filter((p) => p.section === "master")[0];
      setActive(cur);
      setEdit(cur?.content || "");
      setKb(k.data);
    });
  };
  useEffect(load, []);

  const saveNewVersion = async () => {
    if (!canEdit) return;
    if (!edit.trim()) return;
    setSaving(true);
    try {
      // Deactivate current then create new
      if (active) {
        await api.patch(`/prompt-master/${active.id}`, { is_active: false });
      }
      const newVersion = (active?.version || 0) + 1;
      await api.post("/prompt-master", {
        version: newVersion,
        section: "master",
        content: edit,
        reason: reason || "Actualización manual",
        is_active: true,
      });
      toast.success("Nueva versión guardada");
      setReason("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 aura-card-flat p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="label-overline">Prompt Maestro Activo</div>
            <h3 className="font-display text-xl font-medium mt-1">
              Versión {active?.version || 1}
            </h3>
          </div>
          {!canEdit && <span className="pill pill-gray">{isDirector ? "Sólo lectura (Director)" : "Solo lectura"}</span>}
        </div>
        <textarea
          className="aura-input font-mono text-sm"
          rows="15"
          value={edit}
          onChange={(e) => setEdit(e.target.value)}
          disabled={!canEdit}
          data-testid="prompt-master-editor"
        />
        {canEdit && (
          <>
            <input className="aura-input mt-3" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo del cambio (ej: actualización tras campaña...)" />
            <button onClick={saveNewVersion} disabled={saving} className="btn-primary mt-3 flex items-center gap-2" data-testid="save-pm-btn">
              {saving && <span className="spinner" />} Guardar nueva versión
            </button>
          </>
        )}
      </div>

      <div className="aura-card-flat p-6">
        <div className="label-overline mb-3">Base de conocimiento</div>
        <div className="space-y-3 max-h-[600px] overflow-y-auto">
          {kb.length === 0 ? (
            <div className="text-center py-6 text-sm text-[#666]">Sin items</div>
          ) : kb.map((k) => (
            <div key={k.id} className="border-l-2 border-[#D99D26] pl-3 py-1">
              <div className="text-xs uppercase text-[#666] tracking-wider">{k.category}</div>
              <div className="font-medium text-sm">{k.title}</div>
              <div className="text-xs text-[#666] line-clamp-2 mt-1">{k.content}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const LearningsPanel = () => {
  const [items, setItems] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const { user } = useAuth();
  const canApprove = user?.role === "ceo"; // Only CEO can approve learnings

  const load = () => api.get("/ai-learnings").then((r) => setItems(r.data));
  useEffect(load, []);

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const res = await api.post("/aura/analyze-winners", {});
      toast.success("Análisis generado");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Necesitas registrar publicaciones primero");
    } finally { setAnalyzing(false); }
  };

  const approve = async (id) => {
    await api.post(`/aura/approve-learning/${id}`);
    toast.success("Aprendizaje aprobado y aplicado al prompt maestro");
    load();
  };
  const reject = async (id) => {
    await api.post(`/aura/reject-learning/${id}`);
    toast.success("Rechazado");
    load();
  };

  return (
    <div>
      <div className="aura-card-flat p-5 mb-5 flex justify-between items-center">
        <div>
          <div className="font-display text-lg font-medium">Análisis automático de contenido ganador</div>
          <p className="text-sm text-[#666] mt-1">Aura analiza tus publicaciones y sugiere aprendizajes para tu prompt maestro.</p>
        </div>
        <button onClick={analyze} disabled={analyzing} className="btn-primary flex items-center gap-2" data-testid="analyze-btn">
          {analyzing ? <><span className="spinner" /> Analizando...</> : <><Sparkles size={14} /> Analizar ahora</>}
        </button>
      </div>
      {items.length === 0 ? (
        <div className="text-center py-12 aura-card-flat">
          <div className="text-sm text-[#666]">Sin aprendizajes aún. Aura los generará después del análisis.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((l) => (
            <div key={l.id} className="aura-card p-5" data-testid={`learning-${l.id}`}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <div className="label-overline">{l.target_section}</div>
                  <h3 className="font-display text-lg font-medium mt-1">{l.title}</h3>
                </div>
                <span className={`pill ${l.status === "pendiente" ? "pill-yellow" : l.status === "aplicado" ? "pill-green" : "pill-gray"}`}>{l.status}</span>
              </div>
              <div className="markdown-content text-sm max-h-60 overflow-y-auto">
                <Markdown content={l.recommendation} />
              </div>
              {canApprove && l.status === "pendiente" && (
                <div className="flex gap-2 mt-4">
                  <button onClick={() => approve(l.id)} className="btn-primary flex items-center gap-2"><Check size={14} /> Aprobar y aplicar</button>
                  <button onClick={() => reject(l.id)} className="btn-ghost"><X size={14} className="inline mr-1" /> Rechazar</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const HistoryPanel = () => {
  const [versions, setVersions] = useState([]);
  useEffect(() => {
    api.get("/prompt-master").then((r) => {
      setVersions(r.data.sort((a, b) => (b.version || 0) - (a.version || 0)));
    });
  }, []);

  return (
    <div className="space-y-3">
      {versions.length === 0 ? (
        <div className="text-center py-12 aura-card-flat text-sm text-[#666]">Sin versiones</div>
      ) : versions.map((v) => (
        <div key={v.id} className="aura-card-flat p-5" data-testid={`version-${v.version}`}>
          <div className="flex justify-between items-center mb-2">
            <div>
              <span className="label-overline">Versión {v.version}</span>
              {v.is_active && <span className="pill pill-green ml-2 text-[10px]">Activa</span>}
            </div>
            <div className="text-xs text-[#666]">{new Date(v.created_at).toLocaleDateString("es-MX")}</div>
          </div>
          {v.reason && <div className="text-sm text-[#0A0A0A] font-medium mb-2">{v.reason}</div>}
          <pre className="text-xs text-[#666] whitespace-pre-wrap font-mono max-h-40 overflow-y-auto">{v.content}</pre>
        </div>
      ))}
    </div>
  );
};

const WeeklyReportPanel = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [weekStart, setWeekStart] = useState("");
  const [showEmail, setShowEmail] = useState(false);
  const [emails, setEmails] = useState(user?.email || "");
  const [sending, setSending] = useState(false);
  const [insights, setInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);

  const canSend = user?.role === "ceo" || user?.role === "director_marketing";

  const loadHistory = () => {
    api.get("/workflow/weekly-report/history").then((r) => setHistory(r.data)).catch(() => {});
  };
  useEffect(() => { loadHistory(); }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const res = await api.get("/workflow/weekly-report", { params: weekStart ? { week_start: weekStart } : {} });
      setReport(res.data);
      loadHistory();
      toast.success("Reporte generado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  const loadInsights = async () => {
    setLoadingInsights(true);
    try {
      const res = await api.get("/marketing/learning-insights");
      setInsights(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setLoadingInsights(false); }
  };

  const send = async () => {
    const list = emails.split(",").map((s) => s.trim()).filter(Boolean);
    if (list.length === 0) { toast.error("Agrega al menos un email"); return; }
    setSending(true);
    try {
      const res = await api.post("/workflow/weekly-report/send", { emails: list });
      if (res.data.sent?.length) toast.success(`Enviado a ${res.data.sent.length} email(s)`);
      if (res.data.failed?.length) toast.error(`Falló: ${res.data.failed.map((f) => f.email).join(", ")}`);
      setShowEmail(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setSending(false); }
  };

  const m = report?.metrics;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5" data-testid="weekly-report-panel">
      <div>
        <div className="aura-card-flat p-5 mb-5">
          <div className="flex justify-between items-start gap-3 flex-wrap">
            <div>
              <div className="label-overline">Reporte semanal de productividad</div>
              <h3 className="font-display text-xl font-medium mt-1">Métricas + narrativa generada por Aura</h3>
              <p className="text-sm text-[#666] mt-1">Por defecto la semana anterior (Lun–Dom). Aura analiza ideas, guiones, briefs, publicaciones y desempeño por creador.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <input type="date" value={weekStart} onChange={(e) => setWeekStart(e.target.value)} className="aura-input text-xs" placeholder="Inicio semana (lunes)" data-testid="week-start-input" />
              <button onClick={generate} disabled={loading} className="btn-primary text-sm flex items-center gap-2" data-testid="generate-weekly-btn">
                {loading ? <><span className="spinner" /> Analizando...</> : <><Sparkles size={13} /> Generar reporte</>}
              </button>
              {report && canSend && (
                <button onClick={() => setShowEmail(true)} className="btn-outline text-sm flex items-center gap-2" data-testid="send-email-btn">
                  <Mail size={13} /> Enviar por email
                </button>
              )}
            </div>
          </div>
        </div>

        {report && m && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              <MetricCard label="💡 Ideas generadas" value={m.ideas_generated} sub={`${m.ideas_pending} pendientes`} />
              <MetricCard label="🎬 Guiones" value={`${m.scripts_approved}/${m.scripts_generated}`} sub="aprobados / total" />
              <MetricCard label="📋 Briefs cerrados" value={`${m.briefs_closed}/${m.briefs_total}`} sub="completados / total" />
              <MetricCard label="📅 Publicaciones" value={`${m.posts_published}/${m.posts_scheduled}`} sub="publicadas / programadas" />
            </div>
            <div className="aura-card-flat p-6">
              <Markdown content={report.narrative || ""} />
            </div>
          </>
        )}

        {!report && (
          <div className="aura-card-flat p-12 text-center">
            <div className="w-14 h-14 rounded-2xl bg-[#D99D26]/15 text-[#8a5e10] flex items-center justify-center mx-auto mb-4">
              <BarChart3 size={24} />
            </div>
            <h3 className="font-display text-xl font-medium">Genera tu primer reporte</h3>
            <p className="text-[#666] mt-2 mb-5">Aura analizará la semana y te dará un resumen ejecutivo, desempeño por creador y top ideas pendientes a desarrollar.</p>
            <button onClick={generate} disabled={loading} className="btn-primary">
              {loading ? "Generando..." : "Generar reporte semanal"}
            </button>
          </div>
        )}

        {/* Learning Insights from performance scoring */}
        <div className="aura-card-flat p-5 mt-5 bg-gradient-to-br from-[#FFF9E6] to-white border border-[#D99D26]/20">
          <div className="flex justify-between items-center mb-3">
            <div>
              <div className="label-overline">🧠 Aprendizaje de Aura</div>
              <div className="font-display text-base font-medium">Análisis de qué funcionó y qué no</div>
              <p className="text-xs text-[#666] mt-1">Basado en los posts marcados con <strong>Viral / Normal / No funcionó</strong> en el calendario editorial.</p>
            </div>
            <button onClick={loadInsights} disabled={loadingInsights} className="btn-outline text-xs flex items-center gap-1" data-testid="load-insights-btn">
              {loadingInsights ? <span className="spinner" /> : <Sparkles size={12} />} {insights ? "Actualizar" : "Generar"}
            </button>
          </div>
          {insights && (
            <>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-center">
                  <div className="text-2xl font-display font-bold text-green-700">{insights.stats?.viral?.count || 0}</div>
                  <div className="text-[10px] text-green-700">🔥 Viral</div>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 text-center">
                  <div className="text-2xl font-display font-bold text-gray-700">{insights.stats?.normal?.count || 0}</div>
                  <div className="text-[10px] text-gray-700">➖ Normal</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-center">
                  <div className="text-2xl font-display font-bold text-red-700">{insights.stats?.no_funciono?.count || 0}</div>
                  <div className="text-[10px] text-red-700">❌ No funcionó</div>
                </div>
              </div>
              <div className="markdown-content text-xs max-h-96 overflow-y-auto bg-white p-3 rounded-lg border border-[#E5E5E5]" data-testid="insights-narrative">
                <Markdown content={insights.narrative || ""} />
              </div>
            </>
          )}
          {!insights && (
            <p className="text-xs text-[#666]">Marca los resultados de tus posts publicados (botones viral / normal / no funcionó) para que Aura aprenda patrones y mejore futuras propuestas de push, email, guiones y briefs.</p>
          )}
        </div>
      </div>

      {/* History sidebar */}
      <aside className="aura-card-flat p-5 h-fit">
        <div className="label-overline mb-3">Historial</div>
        {history.length === 0 ? (
          <p className="text-xs text-[#666]">Aún no hay reportes guardados.</p>
        ) : (
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.id} onClick={() => setReport(h)} className="text-xs cursor-pointer hover:bg-[#FAFAFA] rounded p-2 -mx-2" data-testid={`history-${h.id}`}>
                <div className="font-medium">{h.week_start} → {h.week_end}</div>
                <div className="text-[#666] mt-0.5">Generado {new Date(h.generated_at).toLocaleString("es-MX")}</div>
                {h.sent_to?.length > 0 && <div className="text-green-700 mt-0.5">✓ Enviado a {h.sent_to.length}</div>}
              </div>
            ))}
          </div>
        )}
      </aside>

      {showEmail && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowEmail(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md p-7">
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-display text-xl font-medium">Enviar reporte por email</h3>
              <button onClick={() => setShowEmail(false)}><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="aura-label">Destinatarios (separados por coma)</label>
                <textarea className="aura-input" rows="3" value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="benjamin@rubi.com, director@rubi.com" data-testid="email-recipients" />
              </div>
              <p className="text-xs text-[#666]">El reporte se envía con tu Resend configurado. Asegúrate de que el dominio esté verificado si usas tu dominio propio.</p>
              <div className="flex justify-end gap-2">
                <button onClick={() => setShowEmail(false)} className="btn-ghost">Cancelar</button>
                <button onClick={send} disabled={sending} className="btn-primary flex items-center gap-2" data-testid="confirm-send-email">
                  {sending && <span className="spinner" />} Enviar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const MetricCard = ({ label, value, sub }) => (
  <div className="aura-card-flat p-4">
    <div className="text-xs text-[#666] font-medium">{label}</div>
    <div className="font-display text-3xl font-medium mt-1">{value}</div>
    {sub && <div className="text-[10px] text-[#666] mt-0.5">{sub}</div>}
  </div>
);

export default AuraAI;
